import React, { useState, useMemo } from 'react';
import { CollectionNewSearchPageTranslations } from '../../../CollectionNewSearchPageTranslations.csharp';
import { CheckBox } from "../../../../Ecommerce/Shared/Checkbox/Checkbox";
import { Collapse } from 'react-collapse';
import { MotifRelationship } from '../../../MotifRelationship';

interface CollectionNestedFilterProps {
    filterAllMotifs: MotifRelationship[];
    filterSelected: string[];
    setFilterSelected: (selected: string[]) => void;
    translations: CollectionNewSearchPageTranslations;
    collapsibleName: string;
    facetCounts: { [key: string]: number };
}

export const CollectionNestedFilter: React.FC<CollectionNestedFilterProps> = ({
    filterAllMotifs,
    filterSelected,
    setFilterSelected,
    translations,
    collapsibleName,
    facetCounts,
}) => {
    if (!filterAllMotifs) {
        return null;
    }
    const [isOpen, setIsOpen] = useState(false);
    const [expandedMotifs, setExpandedMotifs] = useState<{ [key: string]: boolean }>({});

    const toggleCollapsible = () => setIsOpen(!isOpen);

    const motifMap = useMemo(() => {
        const map: { [name: string]: MotifRelationship } = {};
        const buildMap = (motif: MotifRelationship) => {
            map[motif.parentName] = motif;
            motif.children?.forEach(buildMap);
        };
        filterAllMotifs.forEach(buildMap);
        return map;
    }, [filterAllMotifs]);

    const parentMapping = useMemo(() => {
        const mapping: { [childName: string]: string } = {};
        Object.values(motifMap).forEach(motif =>
            motif.children?.forEach(child => (mapping[child.parentName] = motif.parentName))
        );
        return mapping;
    }, [motifMap]);

    const getAncestors = (name: string): string[] => {
        const out: string[] = [];
        let cur = parentMapping[name];
        while (cur) {
            out.push(cur);
            cur = parentMapping[cur];
        }
        return out;
    };

    const getAllDescendants = (name: string): string[] => {
        const node = motifMap[name];
        if (!node?.children?.length) return [];
        const names: string[] = [];
        const stack = [...node.children];
        while (stack.length) {
            const cur = stack.pop()!;
            names.push(cur.parentName);
            cur.children && stack.push(...cur.children);
        }
        return names;
    };

    const hasSelectedDescendant = (name: string, sel: Set<string>) =>
        getAllDescendants(name).some(n => sel.has(n));

    const handleCheckboxChange = (
        motifName: string,
        isChecked: boolean,
        isParent: boolean,
        hasChildren: boolean
    ) => {
        const newSelectedMotifs = new Set<string>(filterSelected);
        const newExpandedMotifs = { ...expandedMotifs };

        if (isChecked) {
            newSelectedMotifs.add(motifName);
            getAncestors(motifName).forEach(a => newSelectedMotifs.delete(a));
            if (isParent || hasChildren) newExpandedMotifs[motifName] = true;
        } else {
            newSelectedMotifs.delete(motifName);

            if (isParent) {
                const descendants = getAllDescendants(motifName);
                descendants.forEach(d => {
                    newSelectedMotifs.delete(d);
                    newExpandedMotifs[d] = false;
                });
                newExpandedMotifs[motifName] = false;

                const parent = parentMapping[motifName];
                if (parent && !newSelectedMotifs.has(parent) && !hasSelectedDescendant(parent, newSelectedMotifs)) {
                    newSelectedMotifs.add(parent);
                    newExpandedMotifs[parent] = true;
                }
            } else {
                const parent = parentMapping[motifName];
                if (parent && !hasSelectedDescendant(parent, newSelectedMotifs)) {
                    newSelectedMotifs.add(parent);
                    newExpandedMotifs[parent] = true;
                }
            }
        }

        setFilterSelected(Array.from(newSelectedMotifs));
        setExpandedMotifs(newExpandedMotifs);
    };

    const isAnyChildSelected = (name: string) =>
        getAllDescendants(name).some(n => filterSelected.includes(n));

    const renderMotifs = (motif: MotifRelationship, level = 0) => {
        const { parentName } = motif;
        const isParent = !!motif.children?.length;

        const checked = isParent
            ? filterSelected.includes(parentName) || isAnyChildSelected(parentName)
            : filterSelected.includes(parentName);

        const expanded = expandedMotifs[parentName] || false;
        const count = facetCounts[parentName] || 0;

        return (
            <div key={parentName} style={{ marginLeft: level * 20 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckBox
                        label={`${parentName} (${count})`}
                        isChecked={checked}
                        setIsChecked={(value: boolean) => handleCheckboxChange(parentName, value, isParent, isParent)}
                        disabled={count === 0}
                    />
                </div>
                {isParent && (expanded || checked) && (
                    <div
                        id={`accordion-item-${parentName.replace(/\s+/g, '-')}-${level}`}
                        aria-labelledby={`accordion-button-${parentName.replace(/\s+/g, '-')}-${level}`}
                    >
                        {motif.children!.map(ch => renderMotifs(ch, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="CollectionNestedFilter__container">
            <button
                className={`CollectionNestedFilter__header ${isOpen ? 'CollectionNestedFilter__header--open' : ''}`}
                onClick={toggleCollapsible}
                aria-expanded={isOpen}
                aria-controls={`accordion-content-${collapsibleName.replace(/\s+/g, "-")}`}
                id={`accordion-button-${collapsibleName.replace(/\s+/g, "-")}`}
            >
                <p className="CollectionNestedFilter__title">
                    {collapsibleName}
                    {filterSelected.length > 0 && (
                        <span className="CollectionNestedFilter__active-count">
                            ({filterSelected.length} aktiv{filterSelected.length === 1 ? '' : 'e'})
                        </span>
                    )}
                </p>
                <span className="CollectionNestedFilter__icon" />
            </button>
            <Collapse isOpened={isOpen}>
                <div
                    id={`accordion-content-${collapsibleName.replace(/\s+/g, "-")}`}
                    className={'CollectionNestedFilter__content'}
                    aria-labelledby={`accordion-button-${collapsibleName.replace(/\s+/g, "-")}`}>
                    {filterAllMotifs.map((motifCategory) => renderMotifs(motifCategory))}
                </div>
            </Collapse>
        </div>
    );
};
